<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { toast } from 'vue3-toastify';
import dartsHubApi from '@/axios/dartsHubApi';
import { backend_url } from '@/config/config';
import { normalizeForSearch } from '@/functions/utils';

interface Rep { email: string; label: string; }
interface Ligue { id: number; code: string; name: string; }
interface Comite {
  id: number;
  code: string;
  name: string;
  ligue_code: string | null;
  logo?: string | null;
  // Fiche du comité (ComiteProfile), renvoyée aux seuls rôles admin.
  contact_email?: string | null;
  comite_postal_code?: string | null;
  comite_city?: string | null;
  contact_nom?: string | null;
  contact_telephone?: string | null;
  representatives: Rep[];
  premium_events_enabled: boolean;
  premium_events_until?: string | null;
  custom_domain?: string | null;
  affiliation_enabled: boolean;
}

// Lecture seule : les accès premium (et leur durée) se gèrent depuis l'écran unique
// /admin/events-premium, cf. AdminEventsPremium.vue.
const premiumBadge = (c: { premium_events_enabled: boolean; premium_events_until?: string | null }) => {
  if (!c.premium_events_enabled) return { label: 'Standard', cls: 'badge-gray' };
  if (!c.premium_events_until) return { label: 'Premium · sans limite', cls: 'badge-green' };
  const until = new Date(c.premium_events_until);
  const expired = until < new Date(new Date().toDateString());
  return { label: `${expired ? 'Expiré le' : 'Premium jusqu\'au'} ${until.toLocaleDateString('fr-FR')}`, cls: expired ? 'badge-red' : 'badge-green' };
};

const logoUrl = (logo?: string | null) => (logo?.startsWith('/uploads/') ? `${backend_url}${logo}` : logo || '');

const comites = ref<Comite[]>([]);
const ligues = ref<Ligue[]>([]);
const loading = ref(true);
const search = ref('');
const filterLigue = ref('');

const showRepModal = ref(false);
const repComite = ref<Comite | null>(null);
const newRepEmail = ref('');
const savingRep = ref(false);

// Création/modification (super admin). Code et ligue figés après création : recopiés en
// texte sur les événements et les licences (cf. clubs/schemas.py::OrgUpdate).
const showFormModal = ref(false);
const formComite = ref<Comite | null>(null);
const emptyForm = () => ({
  code: '', name: '', ligue_id: null as number | null,
  contact_email: '', postal_code: '', city: '', contact_nom: '', contact_telephone: '',
  custom_domain: '',
  affiliation_enabled: false,
});
const form = ref(emptyForm());
const formLogoFile = ref<File | null>(null);
const savingForm = ref(false);

const openFormModal = (comite: Comite | null) => {
  formComite.value = comite;
  form.value = comite
    ? {
        code: comite.code, name: comite.name, ligue_id: ligues.value.find(l => l.code === comite.ligue_code)?.id ?? null,
        contact_email: comite.contact_email || '', postal_code: comite.comite_postal_code || '', city: comite.comite_city || '',
        contact_nom: comite.contact_nom || '', contact_telephone: comite.contact_telephone || '',
        custom_domain: comite.custom_domain || '',
        affiliation_enabled: comite.affiliation_enabled,
      }
    : emptyForm();
  formLogoFile.value = null;
  showFormModal.value = true;
};

const saveForm = async () => {
  if (!form.value.name.trim() || (!formComite.value && (!form.value.code.trim() || !form.value.ligue_id))) {
    toast.error('Code, nom et ligue requis.');
    return;
  }
  savingForm.value = true;
  const { code, ligue_id, affiliation_enabled, custom_domain, ...fields } = form.value;
  try {
    const { data } = formComite.value
      ? await dartsHubApi.patch(`/clubs/comites/${formComite.value.id}`, { ...fields, affiliation_enabled })
      : await dartsHubApi.post('/clubs/comites', { code, ligue_id, ...fields });
    // Domaine propre (ex. licence.lsef.fr) : à brancher aussi côté serveur (DNS, Traefik).
    if (custom_domain.trim() !== (formComite.value?.custom_domain || '')) {
      try {
        await dartsHubApi.patch(`/branding/comites/${data.id}`, { custom_domain: custom_domain.trim() || null });
      } catch (err: any) {
        toast.warning(`Comité enregistré, mais pas le domaine : ${err.response?.data?.detail || 'erreur'}`);
      }
    }
    if (formLogoFile.value) {
      const fd = new FormData();
      fd.append('file', formLogoFile.value);
      try {
        await dartsHubApi.post(`/clubs/comites/${data.id}/upload-logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } catch {
        toast.warning('Comité enregistré, mais l\'envoi du logo a échoué.');
      }
    }
    toast.success(formComite.value ? 'Comité mis à jour.' : 'Comité créé.');
    showFormModal.value = false;
    await load();
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur.');
  } finally {
    savingForm.value = false;
  }
};

const filtered = computed(() => {
  const q = normalizeForSearch(search.value);
  return comites.value.filter(c =>
    (!filterLigue.value || c.ligue_code === filterLigue.value) &&
    (!q || normalizeForSearch(c.name).includes(q) || normalizeForSearch(c.code).includes(q))
  );
});

const load = async () => {
  loading.value = true;
  try {
    const { data } = await dartsHubApi.get('/clubs/comites');
    comites.value = data;
  } catch {
    toast.error('Erreur de chargement.');
  } finally {
    loading.value = false;
  }
};

const openRepModal = (comite: Comite) => {
  repComite.value = comite;
  newRepEmail.value = '';
  showRepModal.value = true;
};

const addRep = async () => {
  if (!newRepEmail.value || !repComite.value) return;
  savingRep.value = true;
  try {
    await dartsHubApi.post('/clubs/comite-representatives', {
      user_email: newRepEmail.value,
      comite_id: repComite.value.id,
    });
    toast.success('Représentant assigné.');
    newRepEmail.value = '';
    await load();
    repComite.value = comites.value.find(c => c.id === repComite.value!.id) ?? null;
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur.');
  } finally {
    savingRep.value = false;
  }
};

const removeRep = async (email: string) => {
  try {
    await dartsHubApi.delete(`/clubs/comite-representatives/${encodeURIComponent(email)}`);
    toast.success('Représentant retiré.');
    await load();
    if (repComite.value) repComite.value = comites.value.find(c => c.id === repComite.value!.id) ?? null;
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur.');
  }
};

onMounted(async () => {
  const { data } = await dartsHubApi.get('/clubs/ligues');
  ligues.value = data;
  await load();
});
</script>

<template>
  <div class="page">
    <div class="page-header">
      <div style="display:flex;align-items:center;gap:0.75rem;flex-wrap:wrap">
        <h1><i class="fa-solid fa-sitemap" /> Comités</h1>
        <button class="btn btn-primary btn-sm" @click="openFormModal(null)">
          <i class="fa-solid fa-plus" /> Créer un comité
        </button>
      </div>
      <div class="filters">
        <div class="search-bar">
          <i class="fa-solid fa-magnifying-glass" style="color:var(--text-muted)" />
          <input v-model="search" placeholder="Nom ou code…" />
        </div>
        <select v-model="filterLigue" class="filter-select">
          <option value="">Toutes les ligues</option>
          <option v-for="l in ligues" :value="l.code" :key="l.id">{{ l.name }} ({{ l.code }})</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loader"><i class="fa-solid fa-spinner fa-spin" /> Chargement…</div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>Code</th>
            <th>Logo</th>
            <th>Nom</th>
            <th>Ligue</th>
            <th>Contact</th>
            <th>Premium</th>
            <th>Représentants</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="comite in filtered" :key="comite.id">
            <td><span class="badge badge-gray">{{ comite.code }}</span></td>
            <td>
              <img v-if="comite.logo" :src="logoUrl(comite.logo)" class="org-logo" alt="logo" />
              <span v-else class="badge badge-gray">-</span>
            </td>
            <td class="club-name">
              {{ comite.name }}
              <div v-if="comite.comite_city" class="muted-line">{{ comite.comite_postal_code }} {{ comite.comite_city }}</div>
            </td>
            <td>{{ comite.ligue_code || '-' }}</td>
            <td>
              <template v-if="comite.contact_email || comite.contact_nom || comite.contact_telephone">
                <div v-if="comite.contact_email">{{ comite.contact_email }}</div>
                <div v-if="comite.contact_nom || comite.contact_telephone" class="muted-line">
                  {{ [comite.contact_nom, comite.contact_telephone].filter(Boolean).join(' · ') }}
                </div>
              </template>
              <span v-else class="badge badge-gray">-</span>
            </td>
            <td>
              <router-link to="/admin/events-premium" class="badge" :class="premiumBadge(comite).cls"
                title="Gérer les accès premium (écran unique)">
                {{ premiumBadge(comite).label }}
              </router-link>
              <span v-if="comite.affiliation_enabled" class="badge badge-blue" style="margin-left:0.25rem">Affiliations en ligne</span>
            </td>
            <td>
              <div v-if="comite.representatives.length" style="display:flex;flex-wrap:wrap;gap:0.25rem">
                <span v-for="rep in comite.representatives" class="badge badge-blue" :title="rep.email" :key="rep.email">
                  {{ rep.label }}
                </span>
              </div>
              <span v-else class="badge badge-gray">-</span>
            </td>
            <td style="display:flex;gap:0.25rem">
              <button class="btn btn-ghost btn-sm" title="Modifier" @click="openFormModal(comite)">
                <i class="fa-solid fa-pen" />
              </button>
              <button class="btn btn-ghost btn-sm" title="Gérer les représentants" @click="openRepModal(comite)">
                <i class="fa-solid fa-user-tie" />
              </button>
            </td>
          </tr>
          <tr v-if="filtered.length === 0">
            <td colspan="8" style="text-align:center;color:var(--text-muted)">Aucun comité trouvé</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="!loading" class="mobile-cards">
      <div v-for="comite in filtered" class="mobile-card" :key="comite.id">
        <div class="card-top">
          <div style="display:flex;align-items:center;gap:0.6rem">
            <img v-if="comite.logo" :src="logoUrl(comite.logo)" class="org-logo" alt="logo" />
            <div>
              <div style="font-weight:600;font-size:0.95rem">{{ comite.name }}</div>
              <div style="font-size:0.78rem;color:var(--text-muted)">
                {{ comite.code }} · {{ comite.ligue_code || '-' }}<template v-if="comite.comite_city"> · {{ comite.comite_city }}</template>
              </div>
            </div>
          </div>
          <div style="display:flex;gap:0.25rem">
            <button class="btn btn-ghost btn-sm" title="Modifier" @click="openFormModal(comite)">
              <i class="fa-solid fa-pen" />
            </button>
            <button class="btn btn-ghost btn-sm" title="Gérer les représentants" @click="openRepModal(comite)">
              <i class="fa-solid fa-user-tie" />
            </button>
          </div>
        </div>
        <div v-if="comite.contact_email" class="muted-line">
          <i class="fa-solid fa-envelope" /> {{ comite.contact_email }}
        </div>
        <div v-if="comite.contact_nom || comite.contact_telephone" class="muted-line">
          <i class="fa-solid fa-user" /> {{ [comite.contact_nom, comite.contact_telephone].filter(Boolean).join(' · ') }}
        </div>
        <div>
          <router-link to="/admin/events-premium" class="badge" :class="premiumBadge(comite).cls">
            {{ premiumBadge(comite).label }}
          </router-link>
          <span v-if="comite.affiliation_enabled" class="badge badge-blue" style="margin-left:0.25rem">Affiliations en ligne</span>
        </div>
        <div v-if="comite.representatives.length" style="display:flex;flex-wrap:wrap;gap:0.25rem">
          <span v-for="rep in comite.representatives" class="badge badge-blue" :title="rep.email" :key="rep.email">{{ rep.label }}</span>
        </div>
        <div v-else class="badge badge-gray" style="width:fit-content">Aucun représentant</div>
      </div>
      <div v-if="filtered.length === 0" style="text-align:center;color:var(--text-muted);padding:1.5rem">Aucun comité trouvé</div>
    </div>

    <div v-if="!loading" class="table-count">
      {{ filtered.length }} / {{ comites.length }} comités
    </div>

    <!-- Modal création / modification -->
    <div v-if="showFormModal" class="modal-overlay" @click.self="showFormModal = false">
      <div class="modal">
        <div class="modal-header">
          <h2>{{ formComite ? `Modifier le comité ${formComite.code}` : 'Créer un comité' }}</h2>
          <button class="btn btn-ghost btn-sm" @click="showFormModal = false"><i class="fa-solid fa-xmark" /></button>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label>Code</label>
            <input v-model="form.code" type="text" maxlength="6" placeholder="ex: MER" :disabled="!!formComite"
              :title="formComite ? 'Non modifiable : repris sur les événements et les licences' : ''" />
          </div>
          <div class="form-group">
            <label>Nom</label>
            <input v-model="form.name" type="text" placeholder="Nom du comité" />
          </div>
        </div>
        <div class="form-group">
          <label>Ligue</label>
          <select v-model="form.ligue_id" class="filter-select" :disabled="!!formComite">
            <option :value="null">-- Sélectionner --</option>
            <option v-for="l in ligues" :value="l.id" :key="l.id">{{ l.name }} ({{ l.code }})</option>
          </select>
        </div>
        <div class="form-group">
          <label>Adresse de contact (email)</label>
          <input v-model="form.contact_email" type="email" placeholder="contact@comite.fr" />
        </div>
        <div class="form-row">
          <div class="form-group">
            <label>Code postal</label>
            <input v-model="form.postal_code" type="text" inputmode="numeric" maxlength="5" />
          </div>
          <div class="form-group">
            <label>Ville</label>
            <input v-model="form.city" type="text" />
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label>Contact</label>
            <input v-model="form.contact_nom" type="text" placeholder="Nom" />
          </div>
          <div class="form-group">
            <label>Téléphone</label>
            <input v-model="form.contact_telephone" type="tel" />
          </div>
        </div>
        <div v-if="formComite" class="form-group">
          <label class="checkbox-label">
            <input v-model="form.affiliation_enabled" type="checkbox" />
            Affiliations en ligne : le comité reçoit et valide ses demandes sur DartsHub
          </label>
          <small class="muted-line">Les demandes sont envoyées à l'adresse de contact ci-dessus.</small>
        </div>
        <div class="form-group">
          <label>Adresse dédiée de l'espace licences</label>
          <input v-model="form.custom_domain" type="text" placeholder="ex: licence.mondomaine.fr" />
          <small class="muted-line">Facultatif. Sans adresse dédiée : licence.dartshub.fr. Le domaine doit aussi être branché sur le serveur.</small>
        </div>
        <div class="form-group">
          <label>Logo</label>
          <input type="file" accept="image/*" @change="(e) => formLogoFile = (e.target as HTMLInputElement).files?.[0] ?? null" />
        </div>
        <div class="form-actions">
          <button class="btn btn-ghost btn-sm" @click="showFormModal = false">Annuler</button>
          <button class="btn btn-primary btn-sm" :disabled="savingForm" @click="saveForm">
            <i v-if="savingForm" class="fa-solid fa-spinner fa-spin" />
            {{ formComite ? 'Enregistrer' : 'Créer' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal représentants -->
    <div v-if="showRepModal" class="modal-overlay" @click.self="showRepModal = false">
      <div class="modal">
        <div class="modal-header">
          <h2>Représentants : {{ repComite?.name }}</h2>
          <button class="btn btn-ghost btn-sm" @click="showRepModal = false"><i class="fa-solid fa-xmark" /></button>
        </div>
        <div class="form-group">
          <div class="reps-list">
            <div v-if="!repComite?.representatives.length" style="color:var(--text-muted);font-size:0.8rem">Aucun représentant</div>
            <div v-for="rep in repComite?.representatives" class="rep-row" :key="rep.email">
              <span class="badge badge-blue" :title="rep.email">{{ rep.label }}</span>
              <button class="btn btn-ghost btn-sm" title="Retirer" @click="removeRep(rep.email)">
                <i class="fa-solid fa-xmark" />
              </button>
            </div>
          </div>
          <div class="rep-add-row">
            <input v-model="newRepEmail" type="email" placeholder="email@exemple.fr" @keyup.enter="addRep" />
            <button class="btn btn-primary btn-sm" :disabled="savingRep || !newRepEmail" @click="addRep">
              <i v-if="savingRep" class="fa-solid fa-spinner fa-spin" /><i v-else class="fa-solid fa-plus" />
            </button>
          </div>
        </div>
        <div class="form-actions">
          <button class="btn btn-ghost btn-sm" @click="showRepModal = false">Fermer</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.filters {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
}

.filter-select {
  background: var(--bg-input);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  color: var(--text);
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  outline: none;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: 1rem;
  overflow-y: auto;
}

.modal {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 1.5rem;
  min-width: 0;
  max-width: 480px;
  width: 100%;
  max-height: calc(100dvh - 2rem);
  overflow-y: auto;
  margin: auto;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
}

.modal-header h2 {
  font-size: 1rem;
  font-weight: 600;
}

.club-name {
  font-weight: 500;
}

.form-row {
  display: grid;
  grid-template-columns: 7rem 1fr;
  gap: 0.75rem;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.muted-line {
  font-size: 0.78rem;
  font-weight: 400;
  color: var(--text-muted);
}

.org-logo {
  width: 32px;
  height: 32px;
  object-fit: contain;
  border-radius: 4px;
  border: 1px solid var(--border);
  background: var(--bg-card);
}

.table-count {
  margin-top: 0.75rem;
  color: var(--text-muted);
  font-size: 0.8rem;
}

.reps-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
}

.rep-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.rep-add-row {
  display: flex;
  gap: 0.5rem;
}

.rep-add-row input {
  flex: 1;
}

.badge-blue {
  background: rgba(59, 130, 246, 0.15);
  color: #2563eb;
  border: 1px solid rgba(59, 130, 246, 0.3);
}

.mobile-cards { display: none; }

@media (max-width: 640px) {
  .table-wrapper { display: none; }
  .mobile-cards { display: flex; flex-direction: column; gap: 0.75rem; }
}

.mobile-card {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 0.875rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
}
</style>
